import { Box, Button } from '@mui/material';
import { createElement, useState } from 'react';
import Cookies from 'universal-cookie';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import { useTranslation } from 'react-i18next';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import ExoAlert from '../../components/exo/ExoAlert';
import ExoDialog from '../../components/exo/ExoDialog';
import ExtendTimeCorrection from './extended-request-versions/ExtendTimeCorrection';
import ExtendTimeCompensation from './extended-request-versions/ExtendTimeCompensation';
import { ExtendVacation } from './extended-request-versions/ExtendVacation';
import { ExtendSick } from './extended-request-versions/ExtendSick';
import { ExtendTravelExpense } from './extended-request-versions/ExtendTravelExpense';
import NewTravelExpense from './NewTravelExpense';
import RequestEditHistory from '../../components/special/RequestEditHistory';
import NewVacation from './NewVacation';
import NewSick from './NewSick';
import NewTimeCompensation from './NewTimeCompensation';

const cookies = new Cookies();

/**
 * Edit and withdraw for the creator's own pending requests. Editing a travel
 * expense patches its trip in place; every other type supersedes: the form
 * creates a new pending request and the backend declines this one. A time
 * correction references concrete time trackings and has no prefillable form,
 * so it can only be withdrawn.
 */
const OwnRequestActions = ({ request, onUpdate }) => {
  const { t } = useTranslation();
  const config = useConfig();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  // The own list carries no user object (everything in it is own); a
  // management list does, so an absent user counts as own.
  const isOwn =
    !request.user || String(request.user.id) === String(cookies.get('userId'));
  const isPending = parseInt(request.status) === 0;

  const canEdit =
    request.type === 'travelExpense'
      ? Boolean(request.travelExpense)
      : request.type !== 'timeCorrection';

  if (!isOwn || !isPending) return null;

  function handleWithdraw() {
    setConfirmWithdraw(false);
    DBRequest({
      config,
      path: `employeerequests/${request.id}`,
      method: API_PATCH({ status: 2 }),
      onResponse: (res) => onUpdate?.(res.data),
    });
  }

  /** A superseding form declined this request and created a new one. */
  function handleSuperseded(newRequest) {
    onUpdate?.({ ...request, status: 2 });
    if (newRequest) onUpdate?.(newRequest);
  }

  const values = request.values || {};
  const closeEdit = () => setEditOpen(false);

  const editForms = {
    travelExpense: (
      <NewTravelExpense
        travelExpense={request.travelExpense}
        onCreate={(updatedTrip) =>
          onUpdate?.({ ...request, travelExpense: updatedTrip })
        }
        onCancel={closeEdit}
      />
    ),
    vacation: (
      <NewVacation
        existingVacation={request}
        startDataCollection={{
          startDate: values.startDate,
          endDate: values.endDate,
          halfStartDate: Boolean(Number(values.halfStartDate)),
          halfEndDate: Boolean(Number(values.halfEndDate)),
          type: values.type,
          comment: values.comment ?? request.comment,
        }}
        onCreate={handleSuperseded}
        onCancel={closeEdit}
      />
    ),
    sick: (
      <NewSick
        existingRequest={request}
        startDataCollection={{
          startDate: values.startDate,
          endDate: values.endDate,
          comment: request.comment,
        }}
        onCreate={handleSuperseded}
        onCancel={closeEdit}
      />
    ),
    timeCompensation: (
      <NewTimeCompensation
        existingRequest={request}
        startDataCollection={{
          startDate: values.startDate,
          endDate: values.endDate,
          halfStartDate: Boolean(Number(values.halfStartDate)),
          halfEndDate: Boolean(Number(values.halfEndDate)),
          comment: values.comment ?? request.comment,
        }}
        onCreate={handleSuperseded}
        onCancel={closeEdit}
      />
    ),
  };

  return (
    <Box className="flex gap-2 flex-wrap justify-center w-full">
      {canEdit && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setEditOpen(true)}
        >
          {t('Edit')}
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<UndoIcon />}
        onClick={() => setConfirmWithdraw(true)}
      >
        {t('Withdraw request')}
      </Button>

      {canEdit && (
        <ExoDialog
          open={editOpen}
          onClose={closeEdit}
          limitWidth={request.type === 'travelExpense'}
          maxWidth="1250px"
        >
          {editOpen && editForms[request.type]}
        </ExoDialog>
      )}
      <ExoAlert
        open={confirmWithdraw}
        onClose={() => setConfirmWithdraw(false)}
        onClickAway={() => setConfirmWithdraw(false)}
        onSubmit={handleWithdraw}
        alert={{
          type: 'warning',
          message: t(
            'Are you sure you want to withdraw this request? It will be marked as declined.',
          ),
          submitLabel: t('Withdraw request'),
          cancelLabel: t('Cancel'),
        }}
      />
    </Box>
  );
};

const ExtendRequest = ({ request, onUpdate }) => {
  const activeTypes = {
    timeCorrection: ExtendTimeCorrection,
    timeCompensation: ExtendTimeCompensation,
    vacation: ExtendVacation,
    sick: ExtendSick,
    travelExpense: ExtendTravelExpense,
  };

  return (
    <Box className="flex flex-col gap-2 w-full">
      <OwnRequestActions request={request} onUpdate={onUpdate} />
      {createElement(activeTypes[request.type], {
        request: request,
        onUpdate: onUpdate,
      })}
      <RequestEditHistory request={request} />
    </Box>
  );
};

export default ExtendRequest;
